import AppTagStatus from '@nxsys/components/AppTagStatus';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { roles } from '@nxsys/store/constants';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { UserType } from '@nxsys/types/models/User';
import { formatStatus } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tooltip } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { filter } from 'lodash';

type ColumnWithIndex<T> = ColumnType<T> & { index?: number };
import moment from 'moment';

export const getEmployeesColumns = ({
  messages,
  user,
  payrollCountry,
  isEmployer,
  currencySymbol,
  handleViewDetailClick,
  handleAddHours,
  handleOnHold,
  handleResendEmailLoginId,
  handleConfirmDelete,
  handleSendOnboarding,
  isSending,
  selectedResends,
}: {
  messages: TypedMessages;
  user: UserType | null;
  payrollCountry: PayrollCountryType;
  isEmployer: boolean;
  currencySymbol: string;
  handleViewDetailClick: (id: string) => void;
  handleAddHours: (employee: EmployeeRecordType) => void;
  handleOnHold: (employee: EmployeeRecordType) => void;
  handleResendEmailLoginId: (id: string) => void;
  handleConfirmDelete: (employee: EmployeeRecordType | null) => void;
  handleSendOnboarding: (ids: string[]) => void;
  isSending?: boolean;
  selectedResends?: string[];
}): ColumnsType<EmployeeRecordType> => {
  const employerColumn: ColumnWithIndex<EmployeeRecordType>[] = isEmployer
    ? [
        {
          key: 'companyPayment',
          title: 'Employer',
          dataIndex: 'companyPayment',
          width: 280,
          index: 7,
          render: (value) => (
            <div >{value?.companyName || '__'}</div>
          ),
        },
      ]
    : [];

  const ukColumns: ColumnWithIndex<EmployeeRecordType>[] =
    payrollCountry === UNITED_KINGDOM
      ? [
          {
            key: 'NINumber',
            title: 'NI Number',
            dataIndex: 'NINumber',
            width: 150,
            index: 10,
            render: (value) => (
              <div >{value || '__'}</div>
            ),
          },
          {
            key: 'niCategory',
            title: 'NI Category',
            dataIndex: ['setting', 'niCategory'],
            width: 150,
            index: 11,
            render: (value) => (
              <div >{value || '__'}</div>
            ),
          },
          {
            key: 'department',
            title: 'Department',
            dataIndex: 'department',
            width: 150,
            index: 12,
            render: (value) => (
              <div >{value || '__'}</div>
            ),
          },
          {
            key: 'taxCode',
            title: 'Tax Code',
            dataIndex: ['setting', 'taxCode'],
            width: 150,
            index: 13,
            render: (value) => (
              <div >{value || '__'}</div>
            ),
          },
        ]
      : [];

  const irelandColumns: ColumnWithIndex<EmployeeRecordType>[] =
    payrollCountry === IRELAND
      ? [
          {
            key: 'ppsNumber',
            title: 'PPS Number',
            dataIndex: ['setting', 'ppsNumber'],
            width: 150,
            index: 11,
            render: (value) => (
              <div >{value || '__'}</div>
            ),
          },
          {
            key: 'taxBasis',
            title: 'Tax Basis',
            dataIndex: ['setting', 'taxBasis'],
            width: 150,
            index: 12,
            render: (value) => (
              <div >{value || '__'}</div>
            ),
          },
          {
            key: 'department',
            title: 'Department',
            dataIndex: 'department',
            width: 150,
            index: 13,
            render: (value) => (
              <div >{value || '__'}</div>
            ),
          },
        ]
      : [];

  const actionsColumn: ColumnWithIndex<EmployeeRecordType>[] = [
    {
      key: 'actions',
      title: 'Actions',
      fixed: 'left',
      align: 'left',
      width: 150,
      index: 1,
      render: (_, record) => {
        const isSelectedResend = selectedResends?.includes(record.id);

        if (user?.role === roles.agency) {
          return (
            <div
              className="flex items-center justify-start"
              style={{ display: 'flex', flexWrap: 'nowrap', gap: 4 }}
            >
              <Tooltip title="Edit">
                <div
                  className="row_action"
                  onClick={() => handleViewDetailClick(record.id)}
                >
                  <Icons.IconEdit width={15} height={15} />
                </div>
              </Tooltip>
            </div>
          );
        }

        const iconSize = 15;
        return (
          <div
            
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 4,
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Tooltip title="Edit">
              <div
                className="row_action"
                onClick={() => handleViewDetailClick(record.id)}
              >
                <Icons.IconEdit width={iconSize} height={iconSize} />
              </div>
            </Tooltip>
            <Tooltip title={messages['common.addTimesheet']}>
              <div
                className="row_action"
                onClick={() => handleAddHours(record)}
              >
                <Icons.IconFilePlus width={iconSize} height={iconSize} />
              </div>
            </Tooltip>
            <Tooltip title="On Hold">
              <div className="row_action" onClick={() => handleOnHold(record)}>
                <Icons.IconShield
                  width={iconSize}
                  height={iconSize}
                  color={record?.isOnHold ? 'red' : 'green'}
                />
              </div>
            </Tooltip>
            <Tooltip
              title={
                isSending && isSelectedResend
                  ? 'Sending...'
                  : record.errorResendLoginIdFailed
                    ? `Error: ${record.errorResendLoginIdFailed}`
                    : record.isResendLoginId
                      ? 'Resent email successfully.'
                      : 'Resend login Id.'
              }
            >
              <div
                className="row_action"
                onClick={() => handleResendEmailLoginId(record.id)}
              >
                {isSending && isSelectedResend ? (
                  <Icons.IconSend
                    width={iconSize}
                    height={iconSize}
                    color="#ff9900"
                  />
                ) : (
                  <Icons.IconEmail
                    width={iconSize}
                    height={iconSize}
                    color={
                      record.errorResendLoginIdFailed
                        ? 'red'
                        : record.isResendLoginId
                          ? '#22bb00'
                          : '#666666'
                    }
                  />
                )}
              </div>
            </Tooltip>
            <Tooltip title="Delete">
              <div
                className="row_action"
                onClick={() => handleConfirmDelete(record)}
              >
                <Icons.IconTrash width={iconSize} height={iconSize} color="red" />
              </div>
            </Tooltip>
            {!record?.isOnboarding && record?.companyPayment?.subOnboarding && (
              <Tooltip title="Onboarding">
                <div
                  className="row_action"
                  onClick={() => handleSendOnboarding([record.id])}
                >
                  <Icons.IconUserCircle width={iconSize} height={iconSize} />
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const defaultColumns: ColumnWithIndex<EmployeeRecordType>[] = [
    {
      key: 'status',
      title: 'Employee Status',
      dataIndex: 'status',
      align: 'center',
      width: 200,
      index: 2,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <div >
          <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
            {formatStatus(value)}
          </AppTagStatus>
        </div>
      ),
    },
    {
      key: 'id',
      title: messages['table.systemId'] || 'Employee ID',
      dataIndex: 'id',
      width: 150,
      index: 3,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => <div >{value}</div>,
    },
    {
      key: 'fullName',
      title: messages['table.employeeName'] || 'Name',
      dataIndex: 'fullName',
      width: 200,
      index: 4,
      render: (value, record) => (
        <div >
          {value ||
            `${record?.forename || ''} ${record?.surname || ''}` ||
            '__'}
        </div>
      ),
    },
    {
      key: 'email',
      title: 'Email ID',
      dataIndex: 'email',
      width: 300,
      index: 5,
      render: (value) => (
        <div  style={{ wordBreak: 'break-all' }}>
          {value || '__'}
        </div>
      ),
    },
    {
      key: 'mobile',
      title: 'Mobile Number',
      dataIndex: 'mobile',
      width: 150,
      index: 6,
      render: (value) => <div >{value || '__'}</div>,
    },
    {
      key: 'registrationStatus',
      title: 'Registration Status',
      dataIndex: 'registrationStatus',
      align: 'center',
      width: 220,
      index: 8,
      render: (value) => <div >{value || '__'}</div>,
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      width: 150,
      index: 9,
      render: (value) => (
        <div >
          {value &&
          filter(defaultPaymentFrequency, (item) => item?.value === value)
            ? filter(
                defaultPaymentFrequency,
                (item) => item?.value === value,
              )[0]?.label
            : value || '__'}
        </div>
      ),
    },
    {
      key: 'lastPaidDate',
      title: messages['table.lastPaidDate'] || 'Last Paid Date',
      align: 'right',
      width: 150,
      index: 15,
      render: (_, record) => (
        <div >
          {record?.latestPayroll?.paymentDate
            ? moment(record.latestPayroll.paymentDate)
                .tz('Europe/London')
                .format('DD/MM/YYYY')
            : ''}
        </div>
      ),
    },
    {
      key: 'lastPaidPeriod',
      title: 'Last Paid Period',
      align: 'right',
      width: 150,
      index: 16,
      render: (_, record) => (
        <div >
          {record?.latestPayroll
            ? `${record.latestPayroll.taxPeriod}/${record.latestPayroll.taxYear}`
            : ''}
        </div>
      ),
    },
  ];

  const avcColumns: ColumnWithIndex<EmployeeRecordType>[] = [
    {
      key: 'avcEmployer',
      title: 'AVC Employer',
      align: 'right',
      width: 200,
      index: 17,
      render: (_, record) => (
        <div >
          {formatMoney(
            record?.pensionSetting?.avcERContribution,
            currencySymbol,
          )}
        </div>
      ),
    },
    {
      key: 'avcEmployee',
      title: 'AVC Employee',
      align: 'right',
      width: 200,
      index: 18,
      render: (_, record) => (
        <div >
          {formatMoney(
            record?.pensionSetting?.avcEEContribution,
            currencySymbol,
          )}
        </div>
      ),
    },
  ];

  let columns: ColumnWithIndex<EmployeeRecordType>[] = [
    ...actionsColumn,
    ...defaultColumns,
  ];
  if (isEmployer) {
    columns = [...employerColumn, ...columns];
  }
  columns = [...columns, ...ukColumns, ...irelandColumns, ...avcColumns];

  return columns.sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0),
  ) as ColumnsType<EmployeeRecordType>;
};
